/**
 * Registrant portal: operator-issued invoices (paid and unpaid).
 */

import { CheckReceivedViewSheet } from "@/components/non_registrant/check-received-view-sheet";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import {
  REGISTRANT_INVOICES_ACCESSOR_KEYS,
  type RegistrantInvoicesAccessorKeys,
  getRegistrantInvoiceColumnProperties,
} from "@/components/registrant/dashboard/registrant-invoice-list-columns";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  getRegistrantInvoicesOptions,
  getRegistrantInvoicesQueryKey,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { RegistrantPortalInvoiceListItemSchema } from "@/hera-api/client/types.gen";
import {
  cancelRegistrantPayment,
  getRegistrantPaymentDetail,
} from "@/hera-api/manual-check-payments-api";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { refreshLicensesAfterLocalChange } from "@/lib/license-details-change-notification";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

export interface DashboardInvoicesProps {
  isAuthSetupComplete: boolean;
  selectedAms?: string | null;
  /** Page chrome rendered inside the list scroller so it scrolls with the rows. */
  leadingContent?: ReactNode;
}

export function DashboardInvoices({
  isAuthSetupComplete,
  selectedAms = null,
  leadingContent,
}: DashboardInvoicesProps) {
  const queryClient = useQueryClient();
  const { canMakePayments } = useRegistrantOrgPermissions();
  const [viewInvoiceTransactionId, setViewInvoiceTransactionId] = useState<
    string | null
  >(null);
  const [invoiceSheetCancelable, setInvoiceSheetCancelable] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );

  const { data, isLoading, error } = useQuery({
    ...getRegistrantInvoicesOptions(),
    enabled: isAuthSetupComplete,
  });

  const cancelMutation = useMutation({
    mutationFn: (manualTransactionId: string) =>
      cancelRegistrantPayment(manualTransactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getRegistrantInvoicesQueryKey(),
      });
      void refreshLicensesAfterLocalChange(queryClient, []);
      setViewInvoiceTransactionId(null);
      setInvoiceSheetCancelable(false);
      toast({ title: "Success", description: "Invoice cancelled" });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message ?? "Failed to cancel invoice",
        variant: "destructive",
      });
    },
  });

  const handleRegistrantCancelInvoice = useCallback(() => {
    if (!viewInvoiceTransactionId || !invoiceSheetCancelable) {
      return;
    }
    cancelMutation.mutate(viewInvoiceTransactionId);
  }, [cancelMutation, invoiceSheetCancelable, viewInvoiceTransactionId]);

  const invoices = useMemo(() => {
    const rows = data?.invoices ?? [];
    if (!selectedAms) return rows;
    return rows.filter((row) => (row.ams_company_name ?? "") === selectedAms);
  }, [data?.invoices, selectedAms]);

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {leadingContent}
        <div className="flex justify-center items-center py-8">
          <LoadingScreen message="Fetching invoices..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {leadingContent}
        <ErrorComponent
          title="Error loading invoices"
          message={getQueryErrorMessage(error)}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 w-full min-w-0">
      <DashboardTable<
        RegistrantPortalInvoiceListItemSchema,
        RegistrantInvoicesAccessorKeys
      >
        data={invoices}
        leadingContent={
          <div className="pb-4 space-y-4">
            {leadingContent}
            <p className="text-sm text-muted-foreground">
              Invoices issued by your jurisdiction.{" "}
              <span className="font-medium text-foreground">Paid</span> and{" "}
              <span className="font-medium text-foreground">Unpaid</span>{" "}
              include all open and settled invoice balances. Select a row to
              view details, download, or cancel an open invoice.
            </p>
          </div>
        }
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        accessorKeys={REGISTRANT_INVOICES_ACCESSOR_KEYS}
        getColumnProperties={getRegistrantInvoiceColumnProperties}
        getRowId={(row) => row.manual_transaction_id}
        tablePersistenceKey="registrant-invoices"
        initialSortColumn="created_at"
        initialSortDirection="desc"
        onRowClick={(rowId) => {
          const row = invoices.find((r) => r.manual_transaction_id === rowId);
          setInvoiceSheetCancelable(
            !!row &&
              row.payment_state === "unpaid" &&
              row.status === "Payment Pending",
          );
          setViewInvoiceTransactionId(rowId);
        }}
      />
      <CheckReceivedViewSheet
        isOpen={!!viewInvoiceTransactionId}
        onOpenChange={(open) => {
          if (!open) {
            setViewInvoiceTransactionId(null);
            setInvoiceSheetCancelable(false);
          }
        }}
        manualTransactionId={viewInvoiceTransactionId}
        getManualDetail={getRegistrantPaymentDetail}
        printVariant="invoice"
        registrantPortalDocumentDownload
        registrantCancelEnabled={invoiceSheetCancelable && canMakePayments}
        onRegistrantCancel={handleRegistrantCancelInvoice}
        registrantCancelPending={cancelMutation.isPending}
      />
    </div>
  );
}
